import { GTranslate } from '@mui/icons-material';
import { ButtonProps, MenuItem, MenuList, Popover, Skeleton, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { get, map } from 'lodash';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import { UtilsService } from 'src/services/api';
import { EmptyText } from 'src/utils/constants/config';
import { LOCALES, TypeLocale } from 'src/utils/constants/locale';

interface IProps {
  text?: string | null;
  render: (text: string) => ReactNode;
  buttonProps?: ButtonProps;
  isFetching?: boolean;
  disableTranslation?: boolean;
}

type TranslatedMap = Record<string, string>;

const STORAGE_KEY = 'translated_text';

function useTranslatedText() {
  const [map, setMap] = useState<TranslatedMap>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as TranslatedMap) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {}
  }, [map]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        setMap(e.newValue ? (JSON.parse(e.newValue) as TranslatedMap) : {});
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const get = useCallback((key: string) => map[key], [map]);

  const set = useCallback((key: string, value: string) => {
    setMap((prev) => ({ ...prev, [key]: value }));
  }, []);

  const remove = useCallback((key: string) => {
    setMap((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => setMap({}), []);

  const replaceAll = useCallback((entries: TranslatedMap) => {
    setMap({ ...entries });
  }, []);

  return { map, get, set, remove, clear, replaceAll };
}
const TranslateText = ({ text = '', isFetching = false, render, buttonProps, disableTranslation = false }: IProps) => {
  const [language, setLanguage] = useState(new Intl.Locale(navigator.language).language);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { get: getLocalText, set } = useTranslatedText();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => UtilsService.translateText(text ?? '', language),
  });

  const [translateText, setTranslateText] = useState('');

  useEffect(() => {
    if (!text) return;

    if (getLocalText(text)) {
      setTranslateText(getLocalText(text));
      return;
    }
    setTranslateText(text);
  }, [text]);

  const handleTranslate = async () => {
    const res = await mutateAsync();
    if (res.status === HttpStatusCode.Ok) {
      const translatedTextByGoogle = get(res.data, 'data.translations[0].translatedText');
      setTranslateText(translatedTextByGoogle);
      if (text) set(text, translatedTextByGoogle);
    }
  };

  const buttonTranslate = !disableTranslation && (
    <ButtonWithLoading
      sx={{ fontSize: 10, fontWeight: 600, textTransform: 'unset', py: 0.3, pr: 0.5 }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleTranslate();
      }}
      isLoading={isPending}
      isRounded
      color="suggestion"
      variant="outlined"
      size="small"
      startIcon={<GTranslate sx={{ width: 16 }} />}
      {...buttonProps}
    >
      Translate to
      <ButtonWithLoading
        sx={{ fontSize: 10, ml: 1, fontWeight: 600, textTransform: 'unset', py: 0.3, px: 0.5 }}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          e.preventDefault();
          e.stopPropagation();
        }}
        isRounded
        color="suggestion"
        variant="contained"
        size="small"
        endIcon={<ChevronDown size={14} />}
      >
        {new Intl.DisplayNames([language], { type: 'language' }).of(language)}
      </ButtonWithLoading>
    </ButtonWithLoading>
  );

  const handleSelectLang = (locale: TypeLocale) => {
    handleClose();
    setLanguage(locale);
    handleTranslate();
  };
  if (isFetching) return <Skeleton height={30} width="100%" />;

  return (
    <Stack spacing={1} alignItems={'start'}>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList sx={{ maxHeight: 300, minWidth: 300 }}>
          {map(LOCALES, (locale) => {
            const languageNames = new Intl.DisplayNames([locale], { type: 'language' });
            return (
              <MenuItem onClick={() => handleSelectLang(locale)} value={locale}>
                {languageNames.of(locale)}
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
      {render(translateText || EmptyText)} {buttonTranslate}
    </Stack>
  );
};
export default TranslateText;
